import React, { useState, useEffect } from 'react';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {Modal, IconButton, Alert} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import {newTestimonial, userUpdateTestimonial} from "../../../components/business_logic/testimonial_requests";
import {Testimonial} from "../../../models/site";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Rating from "@mui/material/Rating";

interface NewTestimonialProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    existingTestimonial?: Testimonial;
}

export default function NewTestimonial({open, onClose, onSuccess, existingTestimonial}: NewTestimonialProps) {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [rating, setRating] = useState<number>(5);
    const [isAnonymous, setIsAnonymous] = useState(false);
    const [titleError, setTitleError] = useState(false);
    const [titleErrorMessage, setTitleErrorMessage] = useState('');
    const [contentError, setContentError] = useState(false);
    const [contentErrorMessage, setContentErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const isEditMode = !!existingTestimonial;

    // Load existing testimonial data when in edit mode
    useEffect(() => {
        if (open) {
            if (existingTestimonial) {
                setTitle(existingTestimonial.title || '');
                setContent(existingTestimonial.content || '');
                setRating(existingTestimonial.rating || 5);
                setIsAnonymous(existingTestimonial.is_anonymous || false);
            } else {
                resetForm();
            }
        }
    }, [open, existingTestimonial]);

    const resetForm = () => {
        setTitle('');
        setContent('');
        setRating(5);
        setIsAnonymous(false);
        setTitleError(false);
        setTitleErrorMessage('');
        setContentError(false);
        setContentErrorMessage('');
        setSubmitSuccess(false);
        setSubmitError('');
    };

    const handleClose = () => {
        if (!isSubmitting) {
            resetForm();
            onClose();
        }
    };

    const validateInputs = () => {
        let isValid = true;

        if (!title || title.trim().length === 0) {
            setTitleError(true);
            setTitleErrorMessage('Title is required.');
            isValid = false;
        } else {
            setTitleError(false);
            setTitleErrorMessage('');
        }

        if (!content || content.trim().length === 0) {
            setContentError(true);
            setContentErrorMessage('Content is required.');
            isValid = false;
        } else if (content.trim().length < 10) {
            setContentError(true);
            setContentErrorMessage('Content must be at least 10 characters.');
            isValid = false;
        } else {
            setContentError(false);
            setContentErrorMessage('');
        }

        if (rating < 1 || rating > 5) {
            isValid = false;
        }

        return isValid;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateInputs()) {
            return;
        }

        setIsSubmitting(true);
        setSubmitSuccess(false);
        setSubmitError('');

        try {
            const testimonialData: Testimonial | any = {
                title: title.trim(),
                content: content.trim(),
                rating: rating,
                is_anonymous: isAnonymous,
            };

            let result: Testimonial;
            if (isEditMode && existingTestimonial) {
                // Update existing testimonial
                testimonialData.id = existingTestimonial.id;
                result = await userUpdateTestimonial(testimonialData);
            } else {
                // Create new testimonial
                result = await newTestimonial(testimonialData);
            }
            
            if (result && Object.keys(result).length > 0) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    resetForm();
                    onClose();
                    if (onSuccess) {
                        onSuccess();
                    }
                }, 1500);
            } else {
                setSubmitError(`Failed to ${isEditMode ? 'update' : 'create'} testimonial. Please try again.`);
            }
        } catch (error) {
            setSubmitError(`An error occurred while ${isEditMode ? 'updating' : 'creating'} the testimonial.`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal 
            open={open}
            onClose={(event, reason) => {
                if (reason !== 'backdropClick' && !isSubmitting) {
                    handleClose();
                }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Box 
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    position: 'relative',
                    width: "90%",
                    maxWidth: "600px",
                    maxHeight: "90vh",
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    outline: 'none',
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 4,
                }}
            >
                <IconButton
                    onClick={handleClose}
                    disabled={isSubmitting}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        zIndex: 1,
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        },
                        '&:disabled': {
                            backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    {isEditMode ? 'Edit Testimonial' : 'Create Testimonial'}
                </Typography>

                {submitSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Testimonial {isEditMode ? 'updated' : 'created'} successfully!
                    </Alert>
                )}

                {submitError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {submitError}
                    </Alert>
                )}

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="title">Title</FormLabel>
                    <TextField
                        error={titleError}
                        helperText={titleErrorMessage}
                        id="title"
                        type="text"
                        name="title"
                        placeholder="Brief title for your testimonial"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        fullWidth
                        variant="outlined"
                        color={titleError ? 'error' : 'primary'}
                        disabled={isSubmitting}
                        autoFocus
                    />
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="rating">Rating</FormLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Rating
                            value={rating}
                            onChange={(event, newValue) => {
                                if (newValue !== null) {
                                    setRating(newValue);
                                }
                            }}
                            size="large"
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiRating-iconFilled': {
                                    color: 'primary.main'
                                }
                            }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {rating} / 5
                        </Typography>
                    </Box>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                    <FormLabel htmlFor="content">Content</FormLabel>
                    <TextField
                        error={contentError}
                        helperText={contentErrorMessage}
                        id="content"
                        name="content"
                        placeholder="Share your experience with PennyPusher..."
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        required
                        fullWidth
                        multiline
                        rows={6}
                        variant="outlined"
                        color={contentError ? 'error' : 'primary'}
                        disabled={isSubmitting}
                    />
                </FormControl>

                <FormControl fullWidth sx={{ mb: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isAnonymous}
                                onChange={(e) => setIsAnonymous(e.target.checked)}
                                disabled={isSubmitting}
                                color="primary"
                            />
                        }
                        label="Post anonymously"
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -1 }}>
                        If enabled, your username will not be displayed with your testimonial.
                    </Typography>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : (isEditMode ? <EditIcon /> : <AddIcon />)}
                    >
                        {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Testimonial' : 'Create Testimonial')}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}